import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type LoginContext = 'chat' | 'booking' | 'general' | null;

interface RedirectStore {
  redirectUrl: string | null;
  loginContext: LoginContext;
  setRedirectUrl: (url: string | null) => void;
  setLoginContext: (context: LoginContext) => void;
  clearRedirect: () => void;
  hydrated: boolean;
  setHydrated: () => void;
}

export const useRedirectStore = create<RedirectStore>()(
  persist(
    set => ({
      redirectUrl: null,
      loginContext: null,
      setRedirectUrl: url => {
        const currentState = useRedirectStore.getState();
        console.log('💾 [RedirectStore] Setting redirectUrl:', {
          newUrl: url,
          previousUrl: currentState.redirectUrl,
          currentLoginContext: currentState.loginContext,
          timestamp: new Date().toISOString(),
        });
        set({ redirectUrl: url });

        // Verify it was set
        setTimeout(() => {
          const afterSet = useRedirectStore.getState();
          const stored =
            typeof window !== 'undefined'
              ? localStorage.getItem('redirectUrl')
              : null;
          console.log('✅ [RedirectStore] redirectUrl set verification:', {
            inMemory: afterSet.redirectUrl,
            inStorage: stored ? JSON.parse(stored) : null,
            matches: afterSet.redirectUrl === url,
          });
        }, 0);
      },
      setLoginContext: context => {
        const currentState = useRedirectStore.getState();
        console.log('💾 [RedirectStore] Setting loginContext:', {
          newContext: context,
          previousContext: currentState.loginContext,
          currentRedirectUrl: currentState.redirectUrl,
          timestamp: new Date().toISOString(),
        });
        set({ loginContext: context });

        // Verify it was set
        setTimeout(() => {
          const afterSet = useRedirectStore.getState();
          const stored =
            typeof window !== 'undefined'
              ? localStorage.getItem('redirectUrl')
              : null;
          console.log('✅ [RedirectStore] loginContext set verification:', {
            inMemory: afterSet.loginContext,
            inStorage: stored ? JSON.parse(stored)?.state?.loginContext : null,
            matches: afterSet.loginContext === context,
          });
        }, 0);
      },
      clearRedirect: () => {
        console.log('🗑️ [RedirectStore] Clearing redirect');
        set(state => {
          console.log('🗑️ [RedirectStore] Previous state:', {
            redirectUrl: state.redirectUrl,
            loginContext: state.loginContext,
            timestamp: new Date().toISOString(),
          });
          return { redirectUrl: null, loginContext: null };
        });
      },
      hydrated: false,
      setHydrated: () => {
        const state = useRedirectStore.getState();
        console.log('🔄 [RedirectStore] Store hydrated:', {
          redirectUrl: state.redirectUrl,
          loginContext: state.loginContext,
          timestamp: new Date().toISOString(),
        });
        set({ hydrated: true });
      },
    }),
    {
      name: 'redirectUrl',
      onRehydrateStorage: () => {
        console.log('🔄 [RedirectStore] Starting rehydration from storage...');
        return state => {
          if (state) {
            console.log('🔄 [RedirectStore] Rehydration complete:', {
              redirectUrl: state.redirectUrl,
              loginContext: state.loginContext,
              timestamp: new Date().toISOString(),
              // Also check localStorage directly
              localStorageCheck:
                typeof window !== 'undefined'
                  ? localStorage.getItem('redirectUrl')
                  : 'N/A',
            });
          } else {
            console.log(
              '⚠️ [RedirectStore] Rehydration returned null/undefined state'
            );
          }
          state?.setHydrated();
        };
      },
    }
  )
);
